import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import quizApi from '../utils/quizApi';
import DocKeyLink from '../components/DocKeyLink';
import '../styles/Quiz.css';

const PAGE_SIZE = 25;

const STATUS_PILL = {
  draft: ['qz-pill-draft', 'Draft'],
  published: ['qz-pill-live', 'Published'],
  superseded: ['qz-pill-old', 'Superseded'],
};

/* Why the "to do" number can't be given. Each is a different thing to do about it and
   none of them is "nobody owes this" — the three-state honesty rule the training
   funnel's own field signals follow (Critical Note #24). */
const TODO_UNKNOWN = {
  draft: ['Not published', 'A draft is offered to nobody yet.'],
  superseded: ['Retired', 'A later revision’s quiz has taken this one’s place.'],
  no_snapshot: ['Unknown', 'No training report has been built, so who owes this quiz '
                         + 'can’t be worked out. Build the training panel’s report.'],
  unknown_doc: ['Unknown', 'This document is no longer in a built training report, so '
                         + 'who owes the quiz can’t be worked out.'],
  unknown_revision: ['Unknown', 'The training report no longer covers this revision, so '
                              + 'who owed it can’t be listed.'],
};

/**
 * The three numbers on a row, and which of them may honestly be shown.
 *
 * "To do" is only ever printed for a PUBLISHED quiz with a known audience. A draft
 * and a retired quiz are offered to nobody, and an unknown audience is not a zero —
 * printing "0 still to do" when the truth is "we can't tell" is the one direction a
 * compliance screen must never round in.
 */
function progressOf(q) {
  const stats = q.stats || {};
  const pr = q.progress || {};
  const known = !!q.audience_known;
  const offered = q.status === 'published';
  return {
    stats,
    known,
    // Everyone who owes it, when we know; otherwise only the people who have sat it,
    // which is a record of attempts and not a measure of compliance.
    people: known ? (pr.people || 0) : (stats.people || 0),
    passed: known ? (pr.passed || 0) : (stats.passed || 0),
    inProgress: pr.in_progress ?? stats.in_progress ?? 0,
    toDo: known && offered ? (pr.to_do || 0) : null,
    blocked: pr.blocked || 0,
    exhausted: pr.exhausted || 0,
    why: TODO_UNKNOWN[offered ? (q.audience_state || 'no_snapshot') : q.status]
         || TODO_UNKNOWN.no_snapshot,
  };
}

/**
 * The QA-facing quiz library.
 *
 * Paged server-side. The status filter is sent to the API rather than applied to
 * the fetched page, so a page and its total always describe the same set — the
 * same discipline the panel list follows (Critical Note #27).
 */
function QuizList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const status = params.get('status') || '';
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await quizApi.list({ page, limit: PAGE_SIZE, status });
      setRows(d.quizzes || []);
      setTotal(d.total || 0);
      setPages(d.pages || 1);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  // Published, but filed under a revision the training report has moved past. A
  // retired quiz is excluded — being superseded is what retirement IS, not a fault.
  const stranded = rows.filter((q) => q.reach && !['ok', 'retired'].includes(q.reach));

  const setFilter = (next) => {
    // Changing the filter must reset to page 1 — staying on page 3 of a narrower
    // list shows an empty table that reads as "there are no quizzes".
    const p = new URLSearchParams(params);
    if (next) p.set('status', next); else p.delete('status');
    p.delete('page');
    setParams(p, { replace: true });
  };

  const goPage = (n) => {
    const p = new URLSearchParams(params);
    p.set('page', String(n));
    setParams(p, { replace: true });
  };

  return (
    <div className="qz-page">
      <button className="qz-back" onClick={() => navigate('/training')}>← Training</button>

      <div className="qz-head">
        <div>
          <h2>Quizzes</h2>
          <p className="qz-sub">
            One quiz per document revision. Publishing freezes it — a new revision gets its own.
          </p>
        </div>
        <div className="qz-head-actions">
          <button className="qz-btn qz-btn-ghost" onClick={() => navigate('/training/my')}>
            My to-do
          </button>
          <button className="qz-btn qz-btn-primary" onClick={() => navigate('/training/quizzes/new')}>
            New quiz
          </button>
        </div>
      </div>

      <div className="qz-toolbar">
        {[['', 'All'], ['draft', 'Draft'], ['published', 'Published'], ['superseded', 'Superseded']]
          .map(([key, label]) => (
            <button
              key={key || 'all'}
              className={`qz-chip${status === key ? ' active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        <span className="qz-count">
          {total} {total === 1 ? 'quiz' : 'quizzes'}
        </span>
      </div>

      {error && <div className="qz-note qz-note-err"><span>{error}</span></div>}

      {/* A quiz is offered to trainees by document AND revision. When the document is
          revised in Orcanos and the report is rebuilt, a published quiz on the old
          revision stops matching anybody — silently, and while still reading
          "Published" with a perfectly healthy 0 attempts. Counted at the top so it is
          seen without opening every row. */}
      {stranded.length > 0 && (
        <div className="qz-note qz-note-warn">
          <span>⚠</span>
          <div>
            <b>
              {stranded.length === 1
                ? '1 quiz on this page is reaching nobody.'
                : `${stranded.length} quizzes on this page are reaching nobody.`}
            </b>{' '}
            Their document has been revised since they were written, and trainees are
            only offered the quiz filed under the revision they owe. Open one and use
            <b> Start next revision</b> to carry it forward.
          </div>
        </div>
      )}

      {loading ? (
        <div className="qz-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="qz-empty">
          <b>{status ? `No ${status} quizzes` : 'No quizzes yet'}</b>
          <span>
            A quiz is attached to one document revision. Create one, add its questions,
            then publish it — trainees see it on <b>My to-do</b>, and can sit it once they
            have signed that document’s training task.
          </span>
          {status
            ? <button className="qz-btn qz-btn-ghost" onClick={() => setFilter('')}>Show all</button>
            : <button className="qz-btn qz-btn-primary" onClick={() => navigate('/training/quizzes/new')}>New quiz</button>}
        </div>
      ) : (
        <>
          <div className="qz-tablewrap">
            <table className="qz-table">
              <thead>
                <tr>
                  <th>Quiz</th>
                  <th>Attached to</th>
                  <th>Rev</th>
                  <th className="qz-num">Pass</th>
                  <th>Status</th>
                  <th className="qz-num">To do</th>
                  <th className="qz-num">In progress</th>
                  <th>Passed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((q) => {
                  const [cls, label] = STATUS_PILL[q.status] || STATUS_PILL.draft;
                  const p = progressOf(q);
                  const pct = p.people ? Math.round((p.passed / p.people) * 100) : 0;
                  return (
                    <tr
                      key={q.id}
                      className="qz-clickable"
                      onClick={() => navigate(`/training/quizzes/${q.id}`)}
                    >
                      <td>
                        <div className="qz-name">{q.title || q.doc_key}</div>
                        {q.src === 'formal' && <span className="qz-pill qz-pill-formal">Formal</span>}
                      </td>
                      <td>
                        <DocKeyLink docKey={q.doc_key} url={q.doc_url} />
                        {q.doc_name ? <div className="qz-meta">{q.doc_name}</div> : null}
                      </td>
                      <td>
                        {q.revision
                          ? <span className="qz-pill qz-pill-rev">{q.revision}</span>
                          : <span className="qz-meta">—</span>}
                        {/* The document has been revised since — so this quiz matches
                            no trainee at all. Shown next to the revision because the
                            revision IS the mismatch. */}
                        {q.reach === 'stale_revision' && (
                          <div className="qz-blocked-why">
                            Document now at {q.doc_revision || '—'}
                          </div>
                        )}
                      </td>
                      <td className="qz-num">{q.pass_mark}%</td>
                      <td>
                        <span className={`qz-pill ${cls}`}>{label}</span>
                        {/* Status and editability are different things now: a published
                            quiz nobody has started is still editable, and that is exactly
                            the distinction an author needs to see at a glance. */}
                        {q.has_attempts && <div className="qz-meta">Frozen — in use</div>}
                        {/* "Published" and "reaching somebody" are likewise different
                            things, and 0 attempts looks identical either way. */}
                        {q.reach && !['ok', 'retired'].includes(q.reach) && (
                          <div className="qz-blocked-why">Reaching nobody</div>
                        )}
                      </td>
                      {/* Everyone who owes this quiz and hasn't passed it — the number
                          the rest of the row is meaningless without. "1 of 1 passed"
                          reads perfect while forty people who owe the document have
                          never opened it. */}
                      <td className="qz-num">
                        {p.toDo === null
                          ? <span className="qz-meta" title={p.why[1]}>{p.why[0]}</span>
                          : <span className={p.toDo ? 'qz-big due' : 'qz-big'}>{p.toDo}</span>}
                        {/* Waiting on a signature is work the trainee cannot move —
                            the app is holding the door shut (`sit_gate`). Counted in
                            "to do", but said separately so it isn't read as apathy. */}
                        {p.toDo !== null && p.blocked > 0 && (
                          <div className="qz-blocked-why">{p.blocked} awaiting signature</div>
                        )}
                        {p.exhausted > 0 && (
                          <div className="qz-blocked-why">{p.exhausted} out of attempts</div>
                        )}
                      </td>
                      <td className="qz-num">
                        {p.inProgress
                          ? <span className="qz-big open">{p.inProgress}</span>
                          : <span className="qz-meta">—</span>}
                      </td>
                      <td>
                        {p.people ? (
                          <>
                            <div className={`qz-bar${pct < 100 ? ' warn' : ''}`}>
                              <span style={{ width: `${pct}%` }} />
                            </div>
                            <div className="qz-meta">
                              {p.passed} of {p.people}
                              {p.known ? '' : ' who have sat it'}
                            </div>
                          </>
                        ) : (
                          <span className="qz-meta">
                            {p.known ? 'Nobody owes it' : 'Nobody yet'}
                          </span>
                        )}
                        {p.stats.attempts > 0 && (
                          <div className="qz-meta">
                            {p.stats.attempts} attempt{p.stats.attempts === 1 ? '' : 's'}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Where the denominator comes from, said once. Without it "12 to do" reads
              as a number this screen made up, and the first question is always which
              twelve people — which is the report's answer, not the quiz's. */}
          <p className="qz-meta" style={{ marginTop: 12 }}>
            <b>To do</b> is everyone the training report says owes this document
            revision and has not passed — including anyone still waiting to sign their
            training task. Open a quiz to see who. A draft or retired quiz is offered
            to nobody, so it has no to-do count.
          </p>

          {pages > 1 && (
            <div className="qz-pager">
              <button className="qz-btn qz-btn-ghost qz-btn-sm"
                      disabled={page <= 1} onClick={() => goPage(page - 1)}>← Previous</button>
              <span>Page {page} of {pages}</span>
              <button className="qz-btn qz-btn-ghost qz-btn-sm"
                      disabled={page >= pages} onClick={() => goPage(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default QuizList;
